import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Check,
  Info,
  X,
  Shield,
  User,
  Settings,
  RefreshCw,
  Calendar,
  ArrowLeft,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { AuditEntityType } from "@/interfaces/audit-log";
import { useAuditLogs } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";

const AdminLogs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Server-side data fetching
  const {
    data: auditResponse,
    isLoading,
    refetch,
    isError,
  } = useAuditLogs({
    page: currentPage,
    search: searchQuery,
    level: levelFilter,
    category: categoryFilter,
    dateFilter,
  });

  const logs = auditResponse?.data || [];
  const totalItems = auditResponse?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, levelFilter, categoryFilter, dateFilter]);

  // Helpers
  const normalizeLevel = (level?: string) => {
    return level?.toLowerCase() || "info";
  };

  const getLevelIcon = (level: string) => {
    switch (normalizeLevel(level)) {
      case "info":
        return <Info className="w-4 h-4 text-blue-500" />;
      case "success":
        return <Check className="w-4 h-4 text-emerald-500" />;
      case "warning":
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case "error":
        return <X className="w-4 h-4 text-red-500" />;
      default:
        return <Info className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getLevelBadge = (level: string) => {
    switch (normalizeLevel(level)) {
      case "info":
        return (
          <span className="px-2 py-1 rounded text-xs bg-blue-500/10 text-blue-500">
            Info
          </span>
        );
      case "success":
        return (
          <span className="px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500">
            Success
          </span>
        );
      case "warning":
        return (
          <span className="px-2 py-1 rounded text-xs bg-amber-500/10 text-amber-500">
            Warning
          </span>
        );
      case "error":
        return (
          <span className="px-2 py-1 rounded text-xs bg-red-500/10 text-red-500">
            Error
          </span>
        );
      default:
        return <span className="text-muted-foreground">{level}</span>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case AuditEntityType.AUTH:
        return <User className="w-4 h-4" />;
      case AuditEntityType.SECURITY:
        return <Shield className="w-4 h-4" />;
      case AuditEntityType.SETTING:
        return <Settings className="w-4 h-4" />;
      case AuditEntityType.WALLET:
      case AuditEntityType.WALLET_ASSET:
        return <Info className="w-4 h-4" />;
      case AuditEntityType.TRADE:
      case AuditEntityType.TRANSACTION:
      case AuditEntityType.ESCROW:
      case AuditEntityType.SWAP:
        return <RefreshCw className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetch();
      toast({
        title: "Refreshed",
        description: "Logs have been updated.",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExport = () => {
    if (logs.length === 0) {
      toast({
        title: "No Data",
        description: "Nothing to export.",
        variant: "destructive",
      });
      return;
    }

    const csvContent = [
      [
        "ID",
        "Timestamp",
        "Level",
        "Category",
        "Actor Email",
        "Action",
        "Title",
        "Description",
        "IP Address",
        "User Agent",
      ].join(","),

      ...logs.map((log: any) =>
        [
          log.id,
          log.createdAt,
          log.level,
          log.entityType,
          log.actor?.email || "-",
          log.action || "-",
          `"${log.title || "-"}"`,
          `"${log.description || "-"}"`,
          log.trace?.ipAddress || "-",
          `"${log.trace?.userAgent || "-"}"`,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: `${logs.length} logs exported.`,
    });
  };

  const handleViewLog = (log: any) => {
    setSelectedLog(log);
    setShowLogModal(true);
  };

  const categories = Object.values(AuditEntityType);

  if (isLoading && !auditResponse) {
    return <Loader type="bounce" />;
  }

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  return (
    <div className="min-h-screen bg-background lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          className="hidden md:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">System Logs</h1>
            <p className="text-muted-foreground mt-1">
              Monitor platform activity and events
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 bg-card rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Info className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {totalItems}
                </p>
                <p className="text-xs text-muted-foreground">Total Logs</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    logs.filter(
                      (l: any) => normalizeLevel(l.level) === "success",
                    ).length
                  }
                </p>
                <p className="text-xs text-muted-foreground">Success</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Info className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    logs.filter((l: any) => normalizeLevel(l.level) === "info")
                      .length
                  }
                </p>
                <p className="text-xs text-muted-foreground">Info</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    logs.filter(
                      (l: any) => normalizeLevel(l.level) === "warning",
                    ).length
                  }
                </p>
                <p className="text-xs text-muted-foreground">Warnings</p>
              </div>
            </div>
          </div>

          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <X className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    logs.filter((l: any) => normalizeLevel(l.level) === "error")
                      .length
                  }
                </p>
                <p className="text-xs text-muted-foreground">Errors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col xl:flex-row gap-4 mb-6">
          <div className="relative w-full xl:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search logs..."
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />

            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Levels</option>
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>

        {/* Logs Table */}
        <div className="nex-card overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-card">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Level
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Timestamp
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Category
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    User
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Action
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4 hidden lg:table-cell">
                    Details
                  </th>
                  <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-12 text-muted-foreground"
                    >
                      No logs found matching your criteria
                    </td>
                  </tr>
                ) : (
                  logs.map((log: any) => (
                    <tr
                      key={log.id}
                      className="border-b border-border hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          {getLevelIcon(log.level)}
                          {getLevelBadge(log.level)}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-muted-foreground text-sm font-mono">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="py-4 px-4">
                        <span className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-muted-foreground text-xs w-fit">
                          {getCategoryIcon(log.entityType)}
                          {log.entityType}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-foreground font-medium text-sm">
                        {log.actor?.email || "-"}
                      </td>
                      <td className="py-4 px-4 text-foreground font-medium text-sm">
                        {log.title || log.action || "-"}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground text-sm hidden lg:table-cell max-w-xs truncate">
                        {log.description || "-"}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <button
                          onClick={() => handleViewLog(log)}
                          className="p-2 text-muted-foreground hover:text-primary transition-colors"
                          title="View Details"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden space-y-4 p-4">
            {logs.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                No logs found
              </div>
            ) : (
              logs.map((log: any) => (
                <div
                  key={log.id}
                  className="rounded-xl border border-border bg-card p-4"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        {getLevelIcon(log.level)}
                        {getLevelBadge(log.level)}
                      </div>

                      <p className="mt-2 font-medium">
                        {log.title || log.action}
                      </p>

                      <p className="text-xs text-muted-foreground">
                        {new Date(log.createdAt).toLocaleString()}
                      </p>
                    </div>

                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleViewLog(log)}
                    >
                      <Info className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Category</span>

                      <span>{log.entityType}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">User</span>

                      <span className="truncate max-w-[180px]">
                        {log.actor?.email || "-"}
                      </span>
                    </div>

                    <div>
                      <p className="text-muted-foreground mb-1">Description</p>

                      <p className="text-sm">{log.description || "-"}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
              logs
            </p>

            <div className="flex flex-wrap justify-center items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from(
                { length: Math.min(5, totalPages) },
                (_, i) => i + 1,
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    currentPage === page
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Log Detail Modal */}
      {showLogModal && selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl p-4 sm:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <div className="flex items-center gap-3">
                {getLevelIcon(selectedLog.level)}
                <h3 className="text-xl font-bold text-foreground">
                  Log Details
                </h3>
              </div>
              <button
                onClick={() => setShowLogModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Log ID</span>
                <span className="font-mono text-xs break-all">
                  {selectedLog.id}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Timestamp</span>
                <span className="text-foreground font-mono flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4" />
                  {new Date(selectedLog.createdAt).toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Level</span>
                {getLevelBadge(selectedLog.level)}
              </div>

              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Category</span>
                <span className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-muted-foreground text-xs">
                  {getCategoryIcon(selectedLog.entityType)}
                  {selectedLog.entityType}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">User</span>
                <span className="text-foreground">
                  {selectedLog.actor?.email || "-"}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Action</span>
                <span className="text-foreground font-medium">
                  {selectedLog.title || selectedLog.action}
                </span>
              </div>

              <div className="py-2 border-b border-border">
                <span className="text-muted-foreground block mb-1">
                  Description
                </span>
                <p className="text-foreground whitespace-pre-wrap">
                  {selectedLog.description || "-"}
                </p>
              </div>

              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">IP Address</span>
                <span className="text-foreground font-mono text-sm">
                  {selectedLog.trace?.ipAddress || "-"}
                </span>
              </div>

              <div className="py-2">
                <span className="text-muted-foreground block mb-1">
                  User Agent
                </span>
                <p className="break-all text-sm">
                  {selectedLog.trace?.userAgent || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogs;
